import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ff } from "@/lib/feature-flags";
import { HeroSkeleton } from "@/components/ui/Skeleton";

interface SlideData {
  image: string;
  badge?: string;
  title: string;
  titleHighlight?: string;
  subtitle: string;
  ctaText?: string;
  ctaLink?: string;
}

const HeroSection = () => {
  const { data: cmsData, isLoading } = useQuery({
    queryKey: ["hero-slides"],
    queryFn: () => api.cms.heroSlides(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Map CMS data to slide format
  const slides: SlideData[] =
    cmsData?.slides?.map((s) => ({
      image: s.image_url,
      badge: s.badge ?? undefined,
      title: s.title,
      titleHighlight: s.title_highlight ?? undefined,
      subtitle: s.subtitle ?? "",
      ctaText: s.cta_text ?? undefined,
      ctaLink: s.cta_link ?? undefined,
    })) ?? [];

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [
    Autoplay({ delay: 6000, stopOnInteraction: false }),
  ]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  // Loading state
  if (isLoading) {
    if (ff.skeletonScreens) return <HeroSkeleton />;
    return (
      <section className="relative h-screen min-h-[640px] max-h-[1000px] overflow-hidden" style={{ background: "var(--hero-gradient)" }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <h2 className="font-display text-3xl font-bold text-primary-foreground/40">CIEDA</h2>
            <p className="text-primary-foreground/20 mt-2 text-sm">Chargement...</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
      </section>
    );
  }

  // Empty state
  if (slides.length === 0) {
    return (
      <section className="relative h-screen min-h-[640px] max-h-[1000px] overflow-hidden" style={{ background: "var(--hero-gradient)" }}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center max-w-xl px-5">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-primary-foreground/60">CIEDA</h2>
            <p className="text-primary-foreground/40 mt-4 text-lg">
              Import · Export · Distribution Africaine
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
      </section>
    );
  }

  const current = slides[selectedIndex];

  return (
    <section className="relative h-screen min-h-[640px] max-h-[1000px] overflow-hidden">
      {/* Carousel */}
      <div className="absolute inset-0" ref={emblaRef}>
        <div className="flex h-full">
          {slides.map((slide, i) => (
            <div key={i} className="relative flex-[0_0_100%] min-w-0 h-full">
              <img
                src={slide.image}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0" style={{
                background: "linear-gradient(105deg, hsl(25 40% 8% / 0.92) 0%, hsl(25 35% 14% / 0.8) 45%, hsl(25 30% 18% / 0.5) 100%)"
              }} />
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container-max px-5 sm:px-8 lg:px-12 w-full">
          <motion.div
            key={selectedIndex}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-2xl"
          >
            {current.badge && (
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-secondary/15 border border-secondary/25 text-secondary text-sm font-medium mb-8 backdrop-blur-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
                {current.badge}
              </span>
            )}

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-primary-foreground leading-[1.1] mb-6 text-balance">
              {current.title}{" "}
              {current.titleHighlight && (
                <span className="text-gradient-gold">{current.titleHighlight}</span>
              )}
            </h1>

            <p className="text-lg md:text-xl text-primary-foreground/60 max-w-xl mb-10 font-light leading-relaxed">
              {current.subtitle}
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href={current.ctaLink ?? "#catalog"}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-secondary text-secondary-foreground font-semibold text-base hover:bg-gold-light transition-all duration-300 shadow-lg shadow-secondary/20 shine"
              >
                {current.ctaText ?? "Voir le Catalogue"}
                <ArrowRight size={18} />
              </a>
              <a
                href="#about"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-foreground/20 text-primary-foreground font-medium text-base hover:bg-primary-foreground/10 transition-all duration-300 backdrop-blur-sm"
              >
                Découvrir CIEDA
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Carousel Controls */}
      <div className="absolute bottom-4 sm:bottom-8 left-0 right-0 z-20">
        <div className="container-max px-5 sm:px-8 lg:px-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {slides.map((_, i) => (
              <button
                key={i}
                className={`embla-dot ${i === selectedIndex ? "active" : ""}`}
                onClick={() => emblaApi?.scrollTo(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => emblaApi?.scrollPrev()}
              className="w-10 h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-all"
              aria-label="Précédent"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => emblaApi?.scrollNext()}
              className="w-10 h-10 rounded-full border border-primary-foreground/20 flex items-center justify-center text-primary-foreground/60 hover:text-primary-foreground hover:bg-primary-foreground/10 transition-all"
              aria-label="Suivant"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
    </section>
  );
};

export default HeroSection;
